import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useReducedMotion } from '../../../theme/hooks.js';
import type { AnimatedIconProps } from '../../primitives/types.js';

/** Five-point constellation — connecting lines draw themselves in on mount
 *  (paper-easing), then the nodes breathe at offset phases so the pattern
 *  pulses gently like a slow heartbeat. Used as a backdrop ornament on the
 *  hero — small enough to feel like an aside, large enough to read at the
 *  edges of the canvas. */
export function IcnConstellation(props: AnimatedIconProps): ReactNode {
  const { active = true, ...rest } = props;
  const reduced = useReducedMotion();

  // Hand-placed five-node graph — irregular enough to feel celestial.
  const nodes = [
    { cx: 2.5, cy: 11, delay: 0 },
    { cx: 5.5, cy: 5.5, delay: 0.6 },
    { cx: 9, cy: 9, delay: 1.2 },
    { cx: 12, cy: 4, delay: 1.8 },
    { cx: 13.5, cy: 12.5, delay: 2.4 },
  ] as const;

  // Connecting edges, drawn with shared total dasharray for a coherent reveal.
  const edges = [
    { d: 'M2.5 11 L5.5 5.5', len: 7 },
    { d: 'M5.5 5.5 L9 9', len: 5 },
    { d: 'M9 9 L12 4', len: 6 },
    { d: 'M9 9 L13.5 12.5', len: 6 },
  ] as const;

  return (
    <Svg {...rest} iconName="constellation" data-on={active ? 'true' : 'false'}>
      {/* Edges — paper-easing draw-in, staggered by 0.18s each. */}
      <g opacity="0.55">
        {edges.map((e, i) => (
          <path
            key={i}
            d={e.d}
            strokeDasharray={e.len}
            strokeDashoffset={reduced || !active ? 0 : e.len}
          >
            {!reduced && active && (
              <animate
                key={active ? 'on' : 'off'}
                attributeName="stroke-dashoffset"
                from={e.len}
                to="0"
                begin={`${String(0.2 + i * 0.18)}s`}
                dur="0.7s"
                fill="freeze"
                calcMode="spline"
                keySplines="0.22 1 0.36 1"
              />
            )}
          </path>
        ))}
      </g>

      {/* Nodes — solid dots that pulse at offset phases. */}
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={n.cx}
          cy={n.cy}
          r="0.9"
          fill="currentColor"
          stroke="none"
        >
          {!reduced && active && (
            <>
              <animate
                attributeName="r"
                values="0.9;1.4;0.9"
                dur="3.6s"
                begin={`${String(n.delay)}s`}
                repeatCount="indefinite"
                calcMode="spline"
                keyTimes="0;0.5;1"
                keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
              />
              <animate
                attributeName="opacity"
                values="0.55;1;0.55"
                dur="3.6s"
                begin={`${String(n.delay)}s`}
                repeatCount="indefinite"
              />
            </>
          )}
        </circle>
      ))}
    </Svg>
  );
}
